import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from './cn';

export interface SwitchProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onChange' | 'type' | 'role' | 'aria-checked'
  > {
  /** Controlled on/off state. */
  checked: boolean;
  /** Called with the next state when the user toggles the switch. */
  onCheckedChange: (checked: boolean) => void;
  /** Optional label rendered beside the track. */
  label?: ReactNode;
  /** Optional helper text under the label. */
  description?: ReactNode;
  /** Marks the control as invalid for a11y + styling. */
  hasError?: boolean;
}

/**
 * Switch — an accessible on/off toggle (`role="switch"`). Controlled-only: the
 * caller owns `checked` and updates it from `onCheckedChange`. Presentational;
 * no business logic. Mirrors the token/style conventions of `Checkbox`/`Card`.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    onCheckedChange,
    label,
    description,
    hasError = false,
    disabled = false,
    className,
    id,
    ...rest
  },
  ref,
) {
  const control = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-invalid={hasError || undefined}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary border-primary' : 'bg-border border-border',
        hasError && 'border-danger',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-4 w-4 rounded-full bg-bg shadow-sm transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  );

  if (!label && !description) return control;

  return (
    <label className="flex items-start gap-2.5 cursor-pointer select-none">
      {control}
      <span className="flex flex-col">
        {label && <span className="text-sm text-foreground">{label}</span>}
        {description && <span className="text-xs text-muted">{description}</span>}
      </span>
    </label>
  );
});
